import { useMutation } from '@tanstack/react-query';

import { onEditBookFn } from '@/components/admin/lessons/material/Material.hooks';
import { deleteBook, GetBookParams } from '@/data/services/bookServices';
import {
  createChapter,
  updateChaptersOrder,
} from '@/data/services/chapterServices';
import { createSection } from '@/data/services/sectionServices';
import Book from '@/models/Book';
import Chapter from '@/models/Chapter';
import { getErrorMessage } from '@/utils/getErrorMessage';
import alert from '@/utils/shared/alert';
import BookForm from './BookForm';
import {
  createSubchapter,
  updateSubchaptersOrder,
} from '@/data/services/subchapterServices';
import Subchapter from '@/models/Subchapter';

type Props = {
  lessonId: number;
  book: Book;
  onUpdate: (book: Book) => void;
  onEdit: onEditBookFn;
  editing?: boolean;
  readOnly?: boolean;
  updateLessons?: (lessonId?: number) => Promise<void>;
  isLimitedEdition?: boolean;
};

export default function BookService(props: Props) {
  const { book, lessonId, onUpdate, onEdit, editing, updateLessons } = props;

  const { id: bookId, bookType } = book;

  const params: GetBookParams = {
    bookId,
    lessonId,
    bookType,
  };

  const handleAddChapter = async () => {
    const chapters = book.chapters || [];
    const newChapter = await createChapter(params, {
      content: '',
    });

    const newSubchapter = await createSubchapter({
      ...params,
      chapterId: newChapter.id,
    });

    const newSection = await createSection({
      ...params,
      chapterId: newChapter.id,
      subchapterId: newSubchapter.id,
    });

    newSubchapter.sections = [newSection];

    newChapter.items = [newSubchapter];

    chapters.push(newChapter);

    onUpdate({ ...book, chapters });

    return newChapter;
  };

  const { mutateAsync: onAddChapter, isPending: isAddingChapter } = useMutation(
    handleAddChapter,
    {
      onError: err => {
        const errMessage = getErrorMessage(err);
        alert.error(errMessage);
      },
    },
  );
  const handleDeleteChapter = (deleteChapter: Chapter) => {
    const chapters = book.chapters || [];
    const newSections = chapters.filter(
      chapter => chapter.id !== deleteChapter.id,
    );
    onUpdate({ ...book, chapters: newSections });
  };

  const handleUpdateChapter = (chapter: Chapter) => {
    let chapters = book.chapters || [];

    chapters = chapters.map(c => (c.id === chapter.id ? chapter : c));

    onUpdate({ ...book, chapters });
  };

  async function handleDeleteBook() {
    const result = await onEdit?.();

    const { book, lesson } = result;

    const params = {
      bookId: book.id,
      lessonId: lesson.id,
      bookType: book.bookType,
    };

    await deleteBook(params);

    return params;
  }

  const { mutate: onDeleteBook, isPending: isDeletingBook } = useMutation(
    handleDeleteBook,
    {
      onSuccess: data => updateLessons?.(data.lessonId),

      onError: err => {
        const errMessage = getErrorMessage(err);
        alert.error(errMessage);
      },
    },
  );

  const handleOnReorderChapters = (chapters: Chapter[]) => {
    updateChaptersOrder({
      params: params,
      chapters: chapters.map(({ id }) => ({ id })),
    });

    const newChapters = chapters.map((bookChapter, index) => ({
      ...bookChapter,
      order: index + 1,
    }));

    onUpdate({ ...book, chapters: newChapters });
  };

  const handleAddSubchapter = async (chapter: Chapter) => {
    const chapters = book.chapters || [];

    const newSubchapter = await createSubchapter({
      ...params,
      chapterId: chapter.id,
    });

    const newSection = await createSection({
      ...params,
      chapterId: chapter.id,
      subchapterId: newSubchapter.id,
    });

    newSubchapter.sections = [newSection];

    const updatedChapter: Chapter = {
      ...chapter,
      items: [...(chapter.items ?? []), newSubchapter],
    };

    const newChapters = chapters.map(bookChapter =>
      bookChapter.id === updatedChapter.id ? updatedChapter : bookChapter,
    );

    onUpdate({ ...book, chapters: newChapters });

    return newSubchapter;
  };

  const { mutateAsync: onAddSubchapter, isPending: isAddingSubchapter } =
    useMutation(handleAddSubchapter, {
      onError: err => {
        const errMessage = getErrorMessage(err);
        alert.error(errMessage);
      },
    });

  const handleOnReorderSubchapters = (
    chapterId: number,
    subchapters: Subchapter[],
  ) => {
    updateSubchaptersOrder({
      params: { ...params, chapterId },
      subchapters: subchapters.map(({ id }) => ({ id })),
    });

    const chapters = book.chapters;

    const chapter = chapters.find(chapter => chapter.id === chapterId);

    if (!chapter) return;

    const reorderedSubchapters = subchapters.map((subchapter, index) => ({
      ...subchapter,
      order: index + 1,
    }));

    const updatedChapter: Chapter = {
      ...chapter,
      items: reorderedSubchapters,
    };

    const newChapters = chapters.map((bookChapter, index) =>
      bookChapter.id === chapterId
        ? updatedChapter
        : { ...bookChapter, order: index + 1 },
    );

    onUpdate({ ...book, chapters: newChapters });
  };

  return (
    <BookForm
      onDeleteChapter={handleDeleteChapter}
      onDeleteBook={onDeleteBook}
      onUpdateChapter={handleUpdateChapter}
      onAddChapter={onAddChapter}
      onEditBook={onEdit}
      isEditingBook={editing}
      isAddingChapter={isAddingChapter}
      isDeletingBook={isDeletingBook}
      onReorderChapters={handleOnReorderChapters}
      onReorderSubchapter={handleOnReorderSubchapters}
      onAddSubchapter={onAddSubchapter}
      isAddingSubchapter={isAddingSubchapter}
      {...props}
    />
  );
}
